import React, { memo } from "react";
import { Box, type BoxProps } from "@chakra-ui/react";

import SkewedBackground from "./SkewedBackground";

export type PublicSectionVariant = "plain" | "tinted" | "skewed";
export type PublicSectionDensity = "compact" | "standard" | "spacious";

export type PublicSectionProps = Omit<BoxProps, "children"> & {
  children: React.ReactNode;
  variant?: PublicSectionVariant;
  density?: PublicSectionDensity;
  contentProps?: BoxProps;
  skewDirection?: "down" | "up";
};

const PUBLIC_SECTION_DENSITIES = {
  compact: {
    py: { base: "6", md: "8" },
  },
  standard: {
    py: { base: "8", md: "12" },
  },
  spacious: {
    py: { base: "10", md: "16" },
  },
} as const;

const PUBLIC_SECTION_VARIANTS = {
  plain: {
    bg: "moveon.public.surface.base",
  },
  tinted: {
    bg: "moveon.public.surface.subtle",
  },
  skewed: {
    bg: "transparent",
  },
} as const;

function PublicSection({
  children,
  variant = "plain",
  density = "standard",
  contentProps,
  skewDirection = "down",
  maxW = "container.lg",
  ...boxProps
}: PublicSectionProps) {
  const section = (
    <Box
      as="section"
      position="relative"
      {...PUBLIC_SECTION_VARIANTS[variant]}
      {...boxProps}
    >
      <Box
        maxW={maxW}
        mx="auto"
        px={{ base: "4", md: "8" }}
        {...PUBLIC_SECTION_DENSITIES[density]}
        {...contentProps}
      >
        {children}
      </Box>
    </Box>
  );

  if (variant !== "skewed") {
    return section;
  }

  return <SkewedBackground direction={skewDirection}>{section}</SkewedBackground>;
}

export default memo(PublicSection);
